import { ChangeEvent, FC, MouseEvent, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow } from "@mui/material";
import { v4 } from "uuid";

import { TrainReportSortColumn } from "@framework/types";
import { SortDirection } from "@framework/types-collection";

import { ReportDownloadButton } from "../../../../../../components/common/buttons/report-download";
import { ReportDialogButton } from "../../../../../../components/common/buttons/report-dialog";
import { formatDateHumanReadable } from "../../../../../../utils";
import { StyledTablePagination } from "../../ui/table-pagination";
import { useScreenSize } from "../../../../../../utils/screen";
import { SortButton } from "../../../../../../components/common/buttons/sort-button";
import { IReportsResponse } from "./interfaces";
import { StyledHeaderTableCell, StyledTableCell } from "./styled";

export interface IReportsTable {
  reports: IReportsResponse[];
  handleSort: (value: TrainReportSortColumn) => () => void;
  sortColumn: TrainReportSortColumn;
  sortOrder: SortDirection;
}

export const ReportsTable: FC<IReportsTable> = props => {
  const { handleSort, sortColumn, sortOrder, reports } = props;

  const { isExtraLargeScreen } = useScreenSize();
  const { formatMessage } = useIntl();

  const reportsId = "pages.scheme.reports";
  const headersId = `${reportsId}.headers`;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const rows = useMemo(
    () =>
      reports.map(report => {
        const { Name, Date, TemplateName, idReport } = report;

        return {
          name: Name,
          date: formatDateHumanReadable(Date),
          templateName: TemplateName,
          idReport,
        };
      }),
    [reports],
  );

  const handleChangePage = (event: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const nameLabel = formatMessage({ id: `${headersId}.name` });
  const dateLabel = formatMessage({ id: `${headersId}.date` });

  useEffect(() => {
    if (isExtraLargeScreen) {
      setPage(0);
      setRowsPerPage(15);
    } else {
      setPage(0);
      setRowsPerPage(5);
    }
  }, [isExtraLargeScreen]);

  return (
    <TableContainer
      elevation={0}
      component={Paper}
      sx={{ flex: 1, minHeight: 0, overflow: "auto", borderRadius: "8px", border: "1px solid #EBEBF7" }}
    >
      <Table aria-label="reports table" sx={{ minWidth: 500, width: "100%" }} stickyHeader>
        <TableHead>
          <TableRow
            sx={{
              "& .MuiTableCell-root": {
                borderRight: "1px solid #EBEBF7",
                backgroundColor: "#FAFAFF",
                borderColor: "#EBEBF7",
                "&:last-of-type": {
                  borderRight: "none",
                },
              },
            }}
          >
            <StyledHeaderTableCell>
              {formatMessage({ id: `${headersId}.name` })}
              <SortButton
                width={12}
                height={12}
                direction={sortOrder}
                onClick={handleSort(TrainReportSortColumn.Name)}
                isActive={sortColumn === TrainReportSortColumn.Name}
              />
            </StyledHeaderTableCell>
            <StyledHeaderTableCell>
              {formatMessage({ id: `${headersId}.date` })}
              <SortButton
                width={12}
                height={12}
                direction={sortOrder}
                onClick={handleSort(TrainReportSortColumn.Date)}
                isActive={sortColumn === TrainReportSortColumn.Date}
              />
            </StyledHeaderTableCell>
            <StyledHeaderTableCell>
              {formatMessage({ id: `${headersId}.template` })}
              <SortButton
                width={12}
                height={12}
                direction={sortOrder}
                onClick={handleSort(TrainReportSortColumn.TemplateName)}
                isActive={sortColumn === TrainReportSortColumn.TemplateName}
              />
            </StyledHeaderTableCell>
            <StyledHeaderTableCell>{formatMessage({ id: `${headersId}.view` })}</StyledHeaderTableCell>
            <StyledHeaderTableCell>{formatMessage({ id: `${headersId}.download` })}</StyledHeaderTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(rowsPerPage > 0 ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : rows).map(row => (
            <TableRow
              key={v4()}
              sx={{
                "& .MuiTableCell-root": {
                  borderColor: "#EBEBF7",
                  borderRight: "1px solid #EBEBF7",
                  "&:last-of-type": {
                    borderRight: "none",
                  },
                },
              }}
            >
              <StyledTableCell sx={{ minWidth: 70 }}>{row.name}</StyledTableCell>
              <StyledTableCell sx={{ minWidth: 100 }}>{row.date}</StyledTableCell>
              <StyledTableCell sx={{ minWidth: 70 }}>{row.templateName}</StyledTableCell>
              <StyledTableCell sx={{ minWidth: 40 }}>
                {row.idReport ? (
                  <ReportDialogButton
                    idReport={Number(row.idReport)}
                    title={`${nameLabel}: ${row.name}\u00A0\u00A0|\u00A0\u00A0${dateLabel}: ${row.date}`}
                  />
                ) : (
                  formatMessage({ id: `${reportsId}.noReport` })
                )}
              </StyledTableCell>
              <StyledTableCell sx={{ minWidth: 40 }}>
                {row.idReport ? (
                  <ReportDownloadButton idReport={Number(row.idReport)} />
                ) : (
                  formatMessage({ id: `${reportsId}.noReport` })
                )}
              </StyledTableCell>
            </TableRow>
          ))}
          {emptyRows > 0 && (
            <TableRow sx={{ height: 53 * emptyRows }}>
              <TableCell colSpan={8} />
            </TableRow>
          )}
        </TableBody>
        {rows.length > rowsPerPage ? (
          <TableFooter>
            <TableRow
              sx={{
                "& .MuiToolbar-root.MuiTablePagination-toolbar": { minHeight: 30, fontSize: 12 },
                "& .MuiToolbar-root.MuiTablePagination-toolbar p": { fontSize: 12, m: 0 },
                "& .MuiToolbar-root .MuiButtonBase-root": { padding: 0.5 },
              }}
            >
              <StyledTablePagination
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                page={page}
                rowsLength={rows.length}
                rowsPerPage={rowsPerPage}
                colSpan={8}
              />
            </TableRow>
          </TableFooter>
        ) : null}
      </Table>
    </TableContainer>
  );
};
